import { ref, onMounted, onUnmounted } from "vue";

interface InactivityOptions {
  limit?: number; // Tempo massimo di inattività in ms (default: 15 min)
  interval?: number; // Ogni quanto controllare in ms (default: 1 min)
  onInactivity?: () => void; // Callback da eseguire una volta sola dopo timeout
  requireTabHidden?: boolean; // Se true, considera inattivo solo se il tab è nascosto
}

export function useInactivityDetector({
  limit = 15 * 60 * 1000,
  interval = 60 * 1000,
  onInactivity,
  requireTabHidden = true,
}: InactivityOptions = {}) {
  const lastActivity = ref(Date.now());
  const isInactiveTooLong = ref(false);
  let intervalId: number | undefined;
  let callbackTriggered = false;

  function updateLastActivity() {
    // Aggiorna il timestamp dell'ultima attività utente al momento attuale
    // e resetta lo stato di inattività e il flag della callback
    lastActivity.value = Date.now();
    isInactiveTooLong.value = false;
    callbackTriggered = false;
  }

  function checkInactivity() {
    // Calcola il tempo trascorso dall'ultima attività
    const now = Date.now();
    const timeSinceLast = now - lastActivity.value;
    const isHidden = document.visibilityState === "hidden";

    // Determina se è il caso di considerare l'utente inattivo:
    // - se è passato il tempo limite
    // - e (se richiesto) la scheda è nascosta
    const shouldTrigger = timeSinceLast >= limit && (!requireTabHidden || isHidden);

    // Se le condizioni sono soddisfatte e la callback non è ancora stata eseguita,
    // imposta lo stato come inattivo, esegue la callback e segna che è già stata chiamata
    if (shouldTrigger && !callbackTriggered) {
      isInactiveTooLong.value = true;
      if (onInactivity) onInactivity();
      callbackTriggered = true;
    }
  }

  onMounted(() => {
    // Elenco degli eventi che indicano attività dell'utente
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    // Aggiunge i listener per aggiornare il timestamp dell'ultima attività quando l'utente interagisce
    events.forEach((e) => window.addEventListener(e, updateLastActivity));
    // Aggiunge un listener per monitorare i cambi di visibilità
    document.addEventListener("visibilitychange", updateLastActivity);
    // Imposta un controllo periodico per rilevare l'inattività in base all'intervallo specificato
    intervalId = window.setInterval(checkInactivity, interval);
  });

  // rimuove ciò che viene creato nel onMounted
  onUnmounted(() => {
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.removeEventListener(e, updateLastActivity));
    document.removeEventListener("visibilitychange", updateLastActivity);
    if (intervalId) clearInterval(intervalId);
  });

  return {
    isInactiveTooLong,
    resetInactivity: updateLastActivity,
  };
}
